import type { TFunction } from "i18next";
import type { Schema } from "@/shared/components/Form/types";
import {
  countriesAccessTypes,
  platformsAccessTypes,
  platformsTypes,
  usersTypes,
} from "@/shared/schemas/visibilityConditions";
import { countryOptions } from "@/shared/utils/countries";
import {
  TagsAutocompleteQuery,
  TagTypesAutocompleteQuery,
} from "@/partner/entities/tags";
import { CollectionTypesQuery } from "@/partner/entities/contents";

/**
 * Ключи лейблов для опций видимости.
 *
 * Значения (`INCLUDE`/`EXCLUDE`/`ALL`, `NONE`/`GUEST`/…) берутся из
 * `@/shared/schemas/visibilityConditions` — shared-слой общий с admin-SPA и
 * не переводится, поэтому русские лейблы оттуда перекрываются локально этими
 * мапами. Union-типы дублируют наборы значений shared-констант (сами они
 * типизированы как `Record<string, string>`): добавление значения в shared
 * без ключа здесь TS не поймает — на этот случай `makeVisibilityOptions`
 * отрисует опцию самим значением вместо лейбла.
 */
type VisibilityAccessType = "INCLUDE" | "EXCLUDE" | "ALL";
type VisibilityUserType = "NONE" | "GUEST" | "USER" | "ADMIN" | "ALL";

const COUNTRIES_ACCESS_TYPE_LABEL_KEYS: Record<VisibilityAccessType, string> = {
  INCLUDE: "visibility.countriesAccessType.INCLUDE",
  EXCLUDE: "visibility.countriesAccessType.EXCLUDE",
  ALL: "visibility.countriesAccessType.ALL",
};

const PLATFORMS_ACCESS_TYPE_LABEL_KEYS: Record<VisibilityAccessType, string> = {
  INCLUDE: "visibility.platformsAccessType.INCLUDE",
  EXCLUDE: "visibility.platformsAccessType.EXCLUDE",
  ALL: "visibility.platformsAccessType.ALL",
};

const USER_TYPE_LABEL_KEYS: Record<VisibilityUserType, string> = {
  NONE: "visibility.userType.NONE",
  GUEST: "visibility.userType.GUEST",
  USER: "visibility.userType.USER",
  ADMIN: "visibility.userType.ADMIN",
  ALL: "visibility.userType.ALL",
};

/** Опции селекта: набор значений — из shared, лейблы — из локальной мапы ключей. */
const makeVisibilityOptions = (
  source: Record<string, string>,
  labelKeys: Record<string, string>,
  t: TFunction
) =>
  Object.keys(source).map((value) => {
    const labelKey = labelKeys[value];
    return { value, label: labelKey ? t(labelKey) : value };
  });

/**
 * Schema for platform page form
 * Uses block type to group fields into collapsible sections
 * Layout: two columns on medium+ screens, stacked on mobile
 */
export const makePlatformPageFormSchema = (t: TFunction): Schema => ({
  // Левая колонка: основная информация и доступность друг под другом.
  // Три блока md:6 в одном контейнере переносили доступность под самый
  // высокий блок (список компонентов), оставляя пустоту под обложкой.
  _left: {
    type: "column",
    xs: 12,
    md: 6,
    subschema: {
      mainInfo: {
        type: "block",
        subschema: {
          title: {
            type: "text",
            label: t("platformPages.form.titleLabel"),
            required: true,
            xs: 12,
            fullWidth: true,
          },
          path: {
            type: "text",
            label: t("platformPages.form.pathLabel"),
            required: true,
            xs: 12,
            fullWidth: true,
            helperText: t("platformPages.form.pathHelper"),
          },
          description: {
            type: "text",
            label: t("platformPages.form.descriptionLabel"),
            multiline: true,
            rows: 4,
            xs: 12,
            fullWidth: true,
          },
          imageId: {
            type: "image",
            label: t("platformPages.form.imageLabel"),
            imageType: "banner",
            helperText: t("platformPages.form.imageHelper"),
            xs: 12,
            fullWidth: true,
          },
          // isHomepage: {
          //   type: "switch",
          //   label: "Главная страница",
          //   xs: 12,
          //   fullWidth: true,
          // },
          // countryCodes: {
          //   type: "select",
          //   label: "Страны",
          //   helperText:
          //     "Если установлена галочка 'Главная страница', здесь можно выбрать страны, для которых эту страницу нужно показывать на главной",
          //   options: countryOptions,
          //   multiple: true,
          //   xs: 12,
          //   fullWidth: true,
          //   visibleCheck: ({ allValues }) => {
          //     return Boolean(allValues?.isHomepage);
          //   },
          // },
        },
        hideHeader: true,
        xs: 12,
        md: 12,
      },
      _visibility: {
        type: "block",
        subschema: {
          _title: {
            type: "title",
            label: t("platformPages.form.visibilityTitle"),
            xs: 12,
          },
          _countries: {
            type: "title",
            variant: "subtitle1",
            label: t("platformPages.form.countriesTitle"),
            xs: 12,
          },
          "visibilityConditions.countries.type": {
            label: t("platformPages.form.countriesTypeLabel"),
            type: "select",
            xs: 12,
            fullWidth: true,
            required: true,
            options: makeVisibilityOptions(
              countriesAccessTypes,
              COUNTRIES_ACCESS_TYPE_LABEL_KEYS,
              t
            ),
          },
          "visibilityConditions.countries.countryCodes": {
            label: t("platformPages.form.countryCodesLabel"),
            type: "select",
            xs: 12,
            fullWidth: true,
            multiple: true,
            // Названия стран приходят из shared и пока не переводятся (deferred).
            options: countryOptions,
            visibleCheck: ({ allValues }) => {
              return (
                allValues?.visibilityConditions?.countries?.type &&
                allValues.visibilityConditions.countries.type !== "ALL"
              );
            },
          },
          _platforms: {
            type: "title",
            variant: "subtitle1",
            label: t("platformPages.form.platformsTitle"),
            xs: 12,
          },
          "visibilityConditions.applicationPlatforms.type": {
            label: t("platformPages.form.platformsTypeLabel"),
            type: "select",
            xs: 12,
            fullWidth: true,
            required: true,
            options: makeVisibilityOptions(
              platformsAccessTypes,
              PLATFORMS_ACCESS_TYPE_LABEL_KEYS,
              t
            ),
          },
          "visibilityConditions.applicationPlatforms.platforms": {
            label: t("platformPages.form.platformsLabel"),
            type: "select",
            xs: 12,
            fullWidth: true,
            multiple: true,
            // IOS / ANDROID / WEB — латиница, перевод не требуется.
            options: Object.entries(platformsTypes).map(([value, label]) => ({
              label,
              value,
            })),
            visibleCheck: ({ allValues }) => {
              return (
                allValues?.visibilityConditions?.applicationPlatforms?.type &&
                allValues.visibilityConditions.applicationPlatforms.type !==
                  "ALL"
              );
            },
          },
          _users: {
            type: "title",
            variant: "subtitle1",
            label: t("platformPages.form.usersTitle"),
            xs: 12,
          },
          "visibilityConditions.userType": {
            label: t("platformPages.form.userTypeLabel"),
            type: "select",
            xs: 12,
            fullWidth: true,
            required: true,
            options: makeVisibilityOptions(usersTypes, USER_TYPE_LABEL_KEYS, t),
          },
        },
        hideHeader: true,
        xs: 12,
        md: 12,
      },
    },
  },
  // Main information block - left column
  blocks: {
    type: "platformBlocks",
    label: t("platformPages.form.blocksLabel"),
    alwaysOpen: true,
    queries: {
      autocompleteTags: TagsAutocompleteQuery,
      autocompleteTagTypes: TagTypesAutocompleteQuery,
      collectionTypes: CollectionTypesQuery,
    },
    xs: 12,
    md: 6,
  },
});
